import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_BASE } from '../config';
import { useDispatch } from 'react-redux';
import './Signup.css';
import { loginSuccess } from '../slices/authSlice';

const Signup = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [name, setName] = useState('');
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [gender, setGender] = useState('male');
  const [inviteCode, setInviteCode] = useState('');
  const [corporateEmail, setCorporateEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [pincode, setPincode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({
    name: '',
    dob: '',
    corporateEmail: '',
    phone: '',
    address: '',
    pincode: '',
    password: '',
  });
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    navigate('/login', {
      replace: true,
      state: {
        loginMessage:
          'Account setup must be completed by your HR admin. Sign in after your account has been provisioned.',
      },
    });
  }, [navigate]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('google_signup');
      if (!raw) return;
      const g = JSON.parse(raw);
      if (g.name) setName(g.name);
      if (g.email) setCorporateEmail(g.email);
    } catch {
      /* ignore */
    }
  }, []);

  const handleDayChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 2);
    setDay(val);
    if (errors.dob) setErrors((prev) => ({ ...prev, dob: '' }));
  };
  const handleMonthChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 2);
    setMonth(val);
    if (errors.dob) setErrors((prev) => ({ ...prev, dob: '' }));
  };
  const handleYearChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 4);
    setYear(val);
    if (errors.dob) setErrors((prev) => ({ ...prev, dob: '' }));
  };

  const isValidEmailFormat = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim());

  const isDobValid = () => {
    const d = parseInt(day, 10);
    const m = parseInt(month, 10);
    const y = parseInt(year, 10);
    if (!day || !month || !year) return false;
    if (isNaN(d) || isNaN(m) || isNaN(y)) return false;
    if (d < 1 || d > 31) return false;
    if (m < 1 || m > 12) return false;
    const currentYear = new Date().getFullYear();
    if (y < 1900 || y > currentYear) return false;
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nameTrimmed = name.trim();
    const emailTrimmed = corporateEmail.trim();
    const addressTrimmed = address.trim();
    const pincodeTrimmed = pincode.trim();
    const phoneDigits = phone.replace(/\D/g, '');
    const newErrors = { name: '', dob: '', corporateEmail: '', phone: '', address: '', pincode: '', password: '' };
    if (!nameTrimmed) newErrors.name = 'Name is required';
    if (!isDobValid()) newErrors.dob = 'Please enter a valid date of birth (dd/mm/yyyy)';
    if (!emailTrimmed) newErrors.corporateEmail = 'Email address is required';
    else if (!isValidEmailFormat(emailTrimmed)) newErrors.corporateEmail = 'Please enter a valid email address';
    if (!/^\d{10}$/.test(phoneDigits)) newErrors.phone = 'Enter a valid 10-digit mobile number';
    if (!addressTrimmed) newErrors.address = 'Address is required';
    if (!/^\d{6}$/.test(pincodeTrimmed)) newErrors.pincode = 'Enter a valid 6-digit pincode';
    if (password || confirmPassword) {
      if (password.length < 8) newErrors.password = 'Password must be at least 8 characters';
      else if (password !== confirmPassword) newErrors.password = 'Passwords do not match';
    }
    setErrors(newErrors);
    setSubmitError('');
    if (
      newErrors.name ||
      newErrors.dob ||
      newErrors.corporateEmail ||
      newErrors.phone ||
      newErrors.address ||
      newErrors.pincode ||
      newErrors.password
    )
      return;
    const email = sessionStorage.getItem('signup_email') || '';
    if (!email) {
      setSubmitError('Session expired. Please start from the login page.');
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          name: nameTrimmed,
          day,
          month,
          year,
          gender,
          inviteCode: inviteCode.trim() || undefined,
          corporateEmail: emailTrimmed,
          mobile: phoneDigits,
          address: addressTrimmed,
          pincode: pincodeTrimmed,
          ...(password ? { password } : {}),
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSubmitError(data.error || 'Failed to save. Please try again.');
        return;
      }
      if (data.token) {
        const roles = data.roles || { isDoctor: false, isOps: false };
        const authKind = data.authKind || 'member';
        dispatch(loginSuccess({ token: data.token, user: data.user, roles, authKind }));
        sessionStorage.removeItem('signup_email');
        sessionStorage.removeItem('google_signup');
        navigate('/login/onboarding-family', { replace: true });
        return;
      }

      navigate('/dashboard');
    } catch (err) {
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNameChange = (e) => {
    setName(e.target.value);
    if (errors.name) setErrors((prev) => ({ ...prev, name: '' }));
  };

  return (
    <div className="signup-page">
      <div className="signup-bg-shapes" aria-hidden="true">
        <span className="signup-shape signup-shape-1"></span>
        <span className="signup-shape signup-shape-2"></span>
      </div>

      <Link to="/" className="signup-logo">
        <img src="/images/unaffectedLogo.png" alt="" className="signup-logo-image" />
        <span className="signup-logo-text">Unaffected Healthcare</span>
      </Link>

      <div className="signup-card-wrap">
        <div className="signup-card">
          <h1 className="signup-title">One last step</h1>

          <form className="signup-form" onSubmit={handleSubmit}>
            <div className="signup-form-group">
              <label htmlFor="signup-name">Your Name <span className="signup-required">*</span></label>
              <input
                id="signup-name"
                type="text"
                value={name}
                onChange={handleNameChange}
                placeholder="Type your name"
                autoComplete="name"
                className={`signup-input ${errors.name ? 'signup-input-error' : ''}`}
                required
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? 'signup-name-error' : undefined}
              />
              {errors.name && (
                <span id="signup-name-error" className="signup-error" role="alert">{errors.name}</span>
              )}
            </div>

            <div className="signup-form-group">
              <label htmlFor="signup-day">Date of Birth <span className="signup-required">*</span></label>
              <div className="signup-dob-row">
                <input
                  id="signup-day"
                  type="text"
                  inputMode="numeric"
                  value={day}
                  onChange={handleDayChange}
                  placeholder="dd"
                  maxLength={2}
                  className={`signup-input signup-dob-input ${errors.dob ? 'signup-input-error' : ''}`}
                  aria-label="Day"
                  aria-invalid={!!errors.dob}
                />
                <span className="signup-dob-sep">/</span>
                <input
                  id="signup-month"
                  type="text"
                  inputMode="numeric"
                  value={month}
                  onChange={handleMonthChange}
                  placeholder="mm"
                  maxLength={2}
                  className={`signup-input signup-dob-input ${errors.dob ? 'signup-input-error' : ''}`}
                  aria-label="Month"
                  aria-invalid={!!errors.dob}
                />
                <span className="signup-dob-sep">/</span>
                <input
                  id="signup-year"
                  type="text"
                  inputMode="numeric"
                  value={year}
                  onChange={handleYearChange}
                  placeholder="yyyy"
                  maxLength={4}
                  className={`signup-input signup-dob-input signup-dob-year ${errors.dob ? 'signup-input-error' : ''}`}
                  aria-label="Year"
                  aria-invalid={!!errors.dob}
                  aria-describedby={errors.dob ? 'signup-dob-error' : undefined}
                />
              </div>
              {errors.dob && (
                <span id="signup-dob-error" className="signup-error" role="alert">{errors.dob}</span>
              )}
            </div>

            <div className="signup-form-group">
              <span className="signup-label">Gender <span className="signup-required">*</span></span>
              <div className="signup-gender-row">
                <button
                  type="button"
                  className={`signup-gender-btn ${gender === 'male' ? 'active' : ''}`}
                  onClick={() => setGender('male')}
                  aria-pressed={gender === 'male'}
                  aria-label="Male"
                >
                  <span className="signup-gender-icon" aria-hidden="true">♂</span>
                  <span>Male</span>
                </button>
                <button
                  type="button"
                  className={`signup-gender-btn signup-gender-btn-female ${gender === 'female' ? 'active' : ''}`}
                  onClick={() => setGender('female')}
                  aria-pressed={gender === 'female'}
                  aria-label="Female"
                >
                  <span className="signup-gender-icon signup-gender-icon-female" aria-hidden="true">♀</span>
                  <span>Female</span>
                </button>
              </div>
            </div>

            <div className="signup-form-group">
              <label htmlFor="signup-invite">Invite Code (Optional)</label>
              <input
                id="signup-invite"
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                placeholder="Enter Invite Code"
                className="signup-input"
              />
            </div>

            <div className="signup-form-group">
              <label htmlFor="signup-corporate-email">Email address <span className="signup-required">*</span></label>
              <input
                id="signup-corporate-email"
                type="email"
                value={corporateEmail}
                onChange={(e) => {
                  setCorporateEmail(e.target.value);
                  if (errors.corporateEmail) setErrors((prev) => ({ ...prev, corporateEmail: '' }));
                }}
                placeholder="e.g. you@company.com"
                autoComplete="email"
                className={`signup-input ${errors.corporateEmail ? 'signup-input-error' : ''}`}
                required
                aria-invalid={!!errors.corporateEmail}
                aria-describedby={errors.corporateEmail ? 'signup-email-error' : undefined}
              />
              {errors.corporateEmail && (
                <span id="signup-email-error" className="signup-error" role="alert">{errors.corporateEmail}</span>
              )}
            </div>

            <div className="signup-form-group">
              <label htmlFor="signup-phone">Phone number <span className="signup-required">*</span></label>
              <input
                id="signup-phone"
                type="tel"
                inputMode="numeric"
                value={phone}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setPhone(v);
                  if (errors.phone) setErrors((prev) => ({ ...prev, phone: '' }));
                }}
                placeholder="10-digit mobile number"
                autoComplete="tel"
                maxLength={10}
                className={`signup-input ${errors.phone ? 'signup-input-error' : ''}`}
                required
                aria-invalid={!!errors.phone}
                aria-describedby={errors.phone ? 'signup-phone-error' : undefined}
              />
              {errors.phone && (
                <span id="signup-phone-error" className="signup-error" role="alert">{errors.phone}</span>
              )}
            </div>

            <div className="signup-form-group">
              <label htmlFor="signup-address">Address <span className="signup-required">*</span></label>
              <input
                id="signup-address"
                type="text"
                value={address}
                onChange={(e) => {
                  setAddress(e.target.value);
                  if (errors.address) setErrors((prev) => ({ ...prev, address: '' }));
                }}
                placeholder="Enter your address"
                autoComplete="street-address"
                className={`signup-input ${errors.address ? 'signup-input-error' : ''}`}
                required
                aria-invalid={!!errors.address}
                aria-describedby={errors.address ? 'signup-address-error' : undefined}
              />
              {errors.address && (
                <span id="signup-address-error" className="signup-error" role="alert">{errors.address}</span>
              )}
            </div>

            <div className="signup-form-group">
              <label htmlFor="signup-password">Create password (optional)</label>
              <input
                id="signup-password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) setErrors((prev) => ({ ...prev, password: '' }));
                }}
                placeholder="At least 8 characters"
                autoComplete="new-password"
                className={`signup-input ${errors.password ? 'signup-input-error' : ''}`}
              />
            </div>

            <div className="signup-form-group">
              <label htmlFor="signup-confirm-password">Confirm password</label>
              <input
                id="signup-confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (errors.password) setErrors((prev) => ({ ...prev, password: '' }));
                }}
                placeholder="Re-enter password"
                autoComplete="new-password"
                className={`signup-input ${errors.password ? 'signup-input-error' : ''}`}
              />
              {errors.password && (
                <span className="signup-error" role="alert">{errors.password}</span>
              )}
            </div>

            <div className="signup-form-group">
              <label htmlFor="signup-pincode">Pincode <span className="signup-required">*</span></label>
              <input
                id="signup-pincode"
                type="text"
                inputMode="numeric"
                value={pincode}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setPincode(v);
                  if (errors.pincode) setErrors((prev) => ({ ...prev, pincode: '' }));
                }}
                placeholder="6-digit pincode"
                autoComplete="postal-code"
                maxLength={6}
                className={`signup-input ${errors.pincode ? 'signup-input-error' : ''}`}
                required
                aria-invalid={!!errors.pincode}
                aria-describedby={errors.pincode ? 'signup-pincode-error' : undefined}
              />
              {errors.pincode && (
                <span id="signup-pincode-error" className="signup-error" role="alert">{errors.pincode}</span>
              )}
            </div>

            {submitError && (
              <p className="signup-submit-error" role="alert">{submitError}</p>
            )}

            <p className="signup-confidential">
              <span className="signup-confidential-icon" aria-hidden="true">ℹ</span>
              All your information will be kept confidential
            </p>

            <button type="submit" className="signup-submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Continue'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Signup;
